import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/lib';
import { LoginPage } from './pages/LoginPage';
import PostLoginRouter from './components/PostLoginRouter';
import OnboardingPage from './pages/onboarding/OnboardingPage';
import MainLayout from './layouts/MainLayout';
import HomePage from './pages/HomePage';
import BusinessIdeaPage from './pages/BusinessIdeaPage';
import TaskDetailPage from './pages/TaskDetailPage';
import CompletedTasksPage from './pages/CompletedTasksPage';
import SettingsPage from './pages/SettingsPage';
import InvitationPage from './pages/InvitationPage';
import ProjectLinkingPage from './pages/ProjectLinkingPage';

import { LoadingPage } from './components';
import './index.css';

// Lazy load module components for better performance
const ExploreModule = React.lazy(() => import('./modules/explore/ExploreModule'));
const IncubationModule = React.lazy(() => import('./modules/incubation/IncubationModule'));
const LaunchModule = React.lazy(() => import('./modules/launch/LaunchModule'));
const GrowthModule = React.lazy(() => import('./modules/growth/GrowthModule'));

const ModuleLoadingSpinner = () => (
  <LoadingPage
    title="Loading Module..."
    subtitle="Please wait while we load your content"
    size="medium"
  />
);

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <LoadingPage
        title="Initializing Orbit..."
        subtitle="Setting up your workspace"
        size="large"
      />
    );
  }

  return (
    <div className="min-h-screen bg-app">
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={user ? <PostLoginRouter /> : <LoginPage />} />
        <Route
          path="/welcome"
          element={user ? <PostLoginRouter /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/onboarding"
          element={user ? <OnboardingPage /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/projects"
          element={
            user ? <div>Project Selection (Coming Soon)</div> : <Navigate to="/login" replace />
          }
        />

        {/* Invitation routes - public but can redirect to auth */}
        <Route path="/invite/:token" element={<InvitationPage />} />
        <Route
          path="/linking-project"
          element={user ? <ProjectLinkingPage /> : <Navigate to="/login" replace />}
        />

        {/* Main app routes with persistent bottom navigation */}
        <Route path="/" element={user ? <MainLayout /> : <Navigate to="/login" replace />}>
          <Route index element={<Navigate to="/home" replace />} />
          <Route path="home" element={<HomePage />} />
          <Route path="business-idea/:id" element={<BusinessIdeaPage />} />
          <Route path="task/:taskId" element={<TaskDetailPage />} />
          <Route path="completed-tasks" element={<CompletedTasksPage />} />
          <Route path="settings" element={<SettingsPage />} />

          <Route
            path="explore/*"
            element={
              <Suspense fallback={<ModuleLoadingSpinner />}>
                <ExploreModule />
              </Suspense>
            }
          />
          <Route
            path="incubation/*"
            element={
              <Suspense fallback={<ModuleLoadingSpinner />}>
                <IncubationModule />
              </Suspense>
            }
          />
          <Route
            path="launch/*"
            element={
              <Suspense fallback={<ModuleLoadingSpinner />}>
                <LaunchModule />
              </Suspense>
            }
          />
          <Route
            path="growth/*"
            element={
              <Suspense fallback={<ModuleLoadingSpinner />}>
                <GrowthModule />
              </Suspense>
            }
          />
        </Route>

        {/* Legacy routes - redirect to new structure */}
        <Route path="/app/*" element={<Navigate to="/" replace />} />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to={user ? '/welcome' : '/login'} replace />} />
      </Routes>
    </div>
  );
}

export default App;
